import { AutoComplete, Form } from "antd"
import React, { useState } from "react"
import { useDebouncedCallback } from "use-debounce/lib"

import GeoLocationService, { GeoLocation } from "Shared/services/geoLocation.service"
import { getPopupAnchor } from "Shared/utils"
import useStateIfMounted from "Shared/utils/hooks/useStateIfMounted"
import { LoadingOutlined } from "@ant-design/icons"

interface AddressAutocompleteInterface {
    placeholder: string
    onSelect: (e: { label: string; value: string; coordinates: [number, number] }) => void
    className?: string
    label?: string
    name?: string | string[] | number
    formItemKey?: string | number
    initialValue?: any
    rules: any
    [rest: string]: any // For Form.List control
}

export interface LocationForForm {
    label: string
    value: string
    coordinates: [number, number]
}

const AddressAutocomplete: React.FC<AddressAutocompleteInterface> = ({ placeholder, onSelect, className, label, name, formItemKey, initialValue, rules, ...rest }) => {
    const [searchResults, setSearchResults] = useStateIfMounted<LocationForForm[]>([])
    const [isLoading, setIsLoading] = useStateIfMounted<boolean>(false)

    const onLocationSearch = useDebouncedCallback(async (queryString: string) => {
        try {
            const data = await GeoLocationService.getPlacesAutocomplete(queryString)
            if (data.length) setSearchResults(data.map(({ address, point: { coordinates } }: GeoLocation) => ({ label: address, value: address, coordinates })))
        } catch (error) {
            console.error("AddressAutocomplete", error)
        } finally {
            setIsLoading(false)
        }
    }, 1000)

    const handleSelection = (value: string, _option: any) => {
        const result = searchResults.find((item) => item.value === value)
        if (result) {
            onSelect(result)
        }
    }

    const getFormItemValue = (selected: string) => {
        const result = searchResults.find((item) => item.value === selected)
        if (!result) return

        return {
            coordinates: result.coordinates,
            address: result.value,
        }
    }

    return (
        <Form.Item
            className={className ?? ""}
            label={label}
            name={name}
            key={formItemKey}
            initialValue={initialValue}
            rules={rules}
            {...rest}
            getValueFromEvent={getFormItemValue}
            validateTrigger={"onBlur"}
        >
            <AutoComplete
                placeholder={placeholder}
                onSearch={(queryString) => {
                    setIsLoading(true)
                    onLocationSearch.callback(queryString)
                }}
                options={isLoading ? undefined : searchResults}
                onSelect={handleSelection}
                getPopupContainer={getPopupAnchor()}
            >
                {isLoading && (
                    <AutoComplete.Option value={""} key={"loading-option"} disabled>
                        <div className="w-full text-center">
                            <LoadingOutlined />
                        </div>
                    </AutoComplete.Option>
                )}
            </AutoComplete>
        </Form.Item>
    )
}

export default AddressAutocomplete
